import { alpha } from '@mui/material/styles';
import { COLORS } from '../../theme';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Box, Button, Typography } from '@mui/material';
import allForm from './Form';
import { useThemeColors } from '../../hooks/useThemeColors';
import { toast } from 'react-hot-toast';
import { useEditProfile } from '../Query/ProfileQuery';
import { useChangePassword } from '../Query/ChangePasswordQuery';
import { ChangeUserPasswordUserTypeEnum } from '../../client-sdk/api';
import PhoneNumberInput from '../Statefull/Utils/PhoneNumberInput';
import { FieldInputProps, FormikProps } from 'formik';
import { useSubAminEditProfile } from '../Query/SubadminProfileQuery';
import { splitPhoneNumber } from '../HelperFunctions/validationUtils';


type OnClose = { onClose: () => void };

export const EditProfileForm: React.FC<OnClose> = ({ onClose }) => {
  const { mutate: EditProfile, isPending: EditProfileLoading } = useEditProfile();
  const { mutate: EditSubAminProfile, isPending: EditSubAminProfileLoading } = useSubAminEditProfile();
  
  const { warning } = useThemeColors();
  const [initialValues, setInitialValues] = useState({
    name: '',
    email: '',
    mobile: '',
  });
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('+962');
  // const allowedCountryCodes = ['+971', '+966', '+965', '+973', '+974', '+968', '+20', '+962', '+961', '+964', '+92'];

  const loadUserFromLocalStorage = () => {
    try {
      const raw = localStorage.getItem('userData');
      if (!raw) return;
      const data = JSON.parse(raw);
      const user = data?.userDetails || data || {};
      const fullMobile: string = String(user?.mobileNumber || user?.mobile || '');
      const digitsOnly = fullMobile.replace(/\s|-/g, '');
      const isWithCode = digitsOnly.length > 9;

      let extractedCode = isWithCode ? digitsOnly.slice(0, digitsOnly.length - 9) : selectedCountryCode;
      // Normalize to leading + and map 00 prefix to +
      if (typeof extractedCode === 'string') {
        extractedCode = extractedCode.trim();
        if (extractedCode.startsWith('00')) {
          extractedCode = `+${extractedCode.slice(2)}`;
        } else if (!extractedCode.startsWith('+')) {
          extractedCode = `+${extractedCode}`;
        }
      }

      // If not allowed, default to +962
      // const normalizedCode = allowedCountryCodes.includes(extractedCode) ? extractedCode : '+962';
      const { countryCode:normalizedCode, phone:localPart } = splitPhoneNumber(fullMobile);

      // const localPart = isWithCode ? digitsOnly.slice(-10) : (digitsOnly || '');
      setSelectedCountryCode(normalizedCode);
      setInitialValues({
        name: user?.name || user?.fullName || '',
        email: user?.email || '',
        mobile: localPart,
      });
    } catch {
      // ignore malformed storage value
    }
  };

  useEffect(() => {
    loadUserFromLocalStorage();

    const handleUserDataChanged = () => loadUserFromLocalStorage();
    window.addEventListener('userDataChanged', handleUserDataChanged);
    window.addEventListener('storage', handleUserDataChanged);

    return () => {
      window.removeEventListener('userDataChanged', handleUserDataChanged);
      window.removeEventListener('storage', handleUserDataChanged);
    };
  }, []);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('First name is required'),
    email: Yup.string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,3}$/,
        'Please enter a valid email address'
      )
      .required('Email is required'),
    mobile: Yup.string()
      .required('Please enter the mobile number')
      .matches(/^[0-9]+$/, 'Mobile number should contain only numbers')
      .test('len', 'Mobile number must be 9 digits or more', (val) => val?.length >= 9)
      .notOneOf(['0000000000'], 'Mobile number cannot be all zeroes'),
  });

  const formConfig = {
    initialValues,
    validationSchema,
    onSubmit: async (values: any) => {
      const raw = localStorage.getItem('userData');
      const parsed = raw ? JSON.parse(raw) : {};
      const user = parsed?.userDetails || {};
      const id = user?.id || parsed?.id;

      // Ensure 10-digit local part and normalized country code
      const localPart = String(values.mobile || '').replace(/\D/g, '').slice(-10);
      let code = selectedCountryCode || '+962';
      if (code.startsWith('00')) code = `+${code.slice(2)}`;
      if (!code.startsWith('+')) code = `+${code}`;

      const payload = {
        name: values.name,
        email: values.email,
        mobileNumber: `${code}${localPart}`,
      };

      const userTypeRaw: string = (parsed?.userType || user?.userType || '').toString();
      const isSubAdmin = userTypeRaw.toLowerCase() === 'subadmin';

      const runMutation = isSubAdmin ? EditSubAminProfile : EditProfile;

      runMutation(
        { id, data: payload },
        {
          onSuccess: () => {
            try {
              const next = {
                ...(parsed || {}),
                userDetails: {
                  ...(parsed?.userDetails || {}),
                  name: values.name,
                  email: values.email,
                  mobileNumber: `${code}${localPart}`,
                },
              };
              localStorage.setItem('userData', JSON.stringify(next));
              window.dispatchEvent(new Event('userDataChanged'));
            } catch {}
            toast.success('Profile updated');
            onClose();
          },
        }
      );
    },
    fields: [
      { name: 'name', label: 'First Name:', type: 'text', isRequired: true },
      { name: 'email', label: 'Email:', type: 'text', isRequired: true },
      {
        name: 'mobile',
        label: 'Mobile No:',
        type: 'custom',
        isRequired: true,
        component: ({ field, form }: { field: FieldInputProps<any>; form: FormikProps<any> }) => (
          <PhoneNumberInput
            field={field}
            form={form}
            selectedCountryCode={selectedCountryCode}
            onCountryCodeChange={setSelectedCountryCode}
          />
        ),
      },
    ],
    enableReinitialize: true,
    customButton: (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
        <Button
          type="submit"
          variant="contained"
          disabled={EditProfileLoading || EditSubAminProfileLoading}
          sx={{ backgroundColor: COLORS.primary.main, textTransform: 'none', '&:hover': { backgroundColor: alpha(COLORS.primary.main, 0.8) } }}
        >
          {EditProfileLoading || EditSubAminProfileLoading ? 'Updating...' : 'Update'}
        </Button>
        <Button variant="contained" sx={{ bgcolor: warning.main }} onClick={onClose} type="button">
          Cancel
        </Button>
      </Box>
    ),
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Edit Profile</Typography>
      {allForm(formConfig)()}
    </Box>
  );
};

export const ChangePasswordForm: React.FC<OnClose> = ({ onClose }) => {
  const { warning } = useThemeColors();

  const { mutate: changePassword, isPending: changePasswordLoading } = useChangePassword();

  const validationSchema = Yup.object().shape({
    oldPassword: Yup.string().required('Old password is required'),
    newPassword: Yup.string().min(8, 'Minimum 8 characters').required('New password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), ''], 'Passwords must match')
      .required('Confirm password is required'),
  });

  const formConfig = {
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values: any) => {
      try {
        const raw = localStorage.getItem('userData');
        const parsed = raw ? JSON.parse(raw) : {};
        const user = parsed?.userDetails || {};
        const id = user?.id || parsed?.id;

        const payload = {
          currentPassword: values.oldPassword,
          newPassword: values.newPassword,
        };

        const userTypeRaw: string = (parsed?.userType || user?.userType || '').toString();
        const isSubAdmin = userTypeRaw.toLowerCase() === 'subadmin';
        const userTypeEnum = isSubAdmin ? ChangeUserPasswordUserTypeEnum.SubAdmin : ChangeUserPasswordUserTypeEnum.Admin;

        changePassword(
          { id, userType: userTypeEnum, data: payload },
          {
            onSuccess: () => {
              toast.success('Password changed');
              onClose();
            },
          }
        );
      } catch {
        // surface handled by query hook
      }
    },
    fields: [
      { name: 'oldPassword', label: 'Old Password:', type: 'password', isRequired: true },
      { name: 'newPassword', label: 'New Password:', type: 'password', isRequired: true },
      { name: 'confirmPassword', label: 'Confirm Password:', type: 'password', isRequired: true },
    ],
    customButton: (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
        <Button
          type="submit"
          variant="contained"
          sx={{ backgroundColor: COLORS.primary.main, textTransform: 'none', '&:hover': { backgroundColor: alpha(COLORS.primary.main, 0.8) } }}
          disabled={changePasswordLoading}
        >
          {changePasswordLoading ? 'Changing...' : 'Change Password'}
        </Button>
        <Button variant="contained" sx={{ bgcolor: warning.main }} onClick={onClose} type="button">
          Cancel
        </Button>
      </Box>
    ),
  };

  return (
    <Box sx={{ p: 1 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Change Password</Typography>
      {allForm(formConfig)()}
    </Box>
  );
};

export default EditProfileForm;


